import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '../../../components/LoadingButton';
import { SWLoadingButtonOutlined } from '../../../components/SWLoadingButtonOutlined';

const useStyles = makeStyles(() => ({
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  fieldContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  fieldValue: {
    color: '#666',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  placeholder: {
    color: '#999',
    fontStyle: 'italic',
  },
}));

interface PaymentSettingsProps {
  payOnlineUrl: string | null;
  paymentInstructions: string | null;
  dueDateDays: number | null;
  hasStripeConnect: boolean;
  onUpdate: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
}

export const PaymentSettings = ({
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  hasStripeConnect,
  onUpdate,
}: PaymentSettingsProps): JSX.Element => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [localPayOnlineUrl, setLocalPayOnlineUrl] = React.useState(payOnlineUrl || '');
  const [localPaymentInstructions, setLocalPaymentInstructions] = React.useState(
    paymentInstructions || '',
  );
  const [localDueDateDays, setLocalDueDateDays] = React.useState(dueDateDays ?? 21);

  // Reset local state when props change
  React.useEffect(() => {
    setLocalPayOnlineUrl(payOnlineUrl || '');
    setLocalPaymentInstructions(paymentInstructions || '');
    setLocalDueDateDays(dueDateDays ?? 21);
    setIsEditing(false);
  }, [payOnlineUrl, paymentInstructions, dueDateDays]);

  const handleSave = () => {
    // Show confirmation dialog before saving
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(
        localPayOnlineUrl.trim() || null,
        localPaymentInstructions.trim() || null,
        localDueDateDays,
      );
      setShowConfirmDialog(false);
      setIsEditing(false);
      toast.success('Payment settings updated successfully', { closeButton: true });
    } catch (err) {
      // Error toast is handled by the API
      // Reset to original values
      setLocalPayOnlineUrl(payOnlineUrl || '');
      setLocalPaymentInstructions(paymentInstructions || '');
      setLocalDueDateDays(dueDateDays ?? 21);
      setShowConfirmDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setLocalPayOnlineUrl(payOnlineUrl || '');
    setLocalPaymentInstructions(paymentInstructions || '');
    setLocalDueDateDays(dueDateDays ?? 21);
    setIsEditing(false);
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Invoice Payment Settings
          </Typography>
          {!isEditing && (
            <IconButton
              onClick={() => setIsEditing(true)}
              className={classes.editIcon}
              size="small"
            >
              <EditIcon />
            </IconButton>
          )}
          {isEditing && (
            <>
              <IconButton onClick={handleSave} disabled={isSaving} size="small" color="primary">
                {isSaving ? <CircularProgress size={20} /> : <CheckIcon />}
              </IconButton>
              <IconButton onClick={handleCancel} disabled={isSaving} size="small">
                <CloseIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Configure default payment settings for SSW invoice PDFs. These can be overridden when
          generating individual invoices.
        </Typography>

        {/* Pay Online URL */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Pay Online URL
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={localPayOnlineUrl}
              onChange={(e) => setLocalPayOnlineUrl(e.target.value)}
              placeholder="https://example.com/pay"
              helperText="URL where tenants can pay online (e.g., payment portal link)"
              disabled={isSaving}
              autoComplete="off"
            />
          ) : (
            <Typography className={payOnlineUrl ? classes.fieldValue : classes.placeholder}>
              {payOnlineUrl || 'Not configured'}
            </Typography>
          )}
        </Box>

        {/* Comments */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Comments
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              value={localPaymentInstructions}
              onChange={(e) => setLocalPaymentInstructions(e.target.value)}
              placeholder="Enter comments for tenants..."
              helperText="Comments displayed on invoices (e.g., payment instructions, check mailing address)"
              disabled={isSaving}
            />
          ) : (
            <Typography className={paymentInstructions ? classes.fieldValue : classes.placeholder}>
              {paymentInstructions || 'Not configured'}
            </Typography>
          )}
        </Box>

        {/* Due Date Days */}
        <Box>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Default Invoice Due Days
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Default Invoice Due Days"
              value={localDueDateDays}
              onChange={(e) => setLocalDueDateDays(parseInt(e.target.value, 10) || 21)}
              inputProps={{ min: 1, max: 365 }}
              disabled={isSaving}
              helperText="Default days until invoices are due"
            />
          ) : (
            <Typography className={classes.fieldValue}>{dueDateDays ?? 21} days</Typography>
          )}
        </Box>
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelConfirm}>
        <DialogTitle>Update Payment Settings?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            These settings will be used as the default for all future SSW invoices generated for
            this property.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You can still override these values when generating individual invoices.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined onClick={handleCancelConfirm} disabled={isSaving}>
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleConfirmSave} loading={isSaving}>
            Save Settings
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
